import React from 'react';
import { humanizeEntityRef, EntityRefLink, EntityRefLinks } from '@backstage/plugin-catalog-react';
import { Chip } from '@material-ui/core';
import { EntityTableRow } from './types';
import { OverflowTooltip, TableColumn } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import { JsonArray } from '@backstage/types';

// The columnFactories symbol is not directly exported, but through the
// CatalogTable.columns field.
/** @public */
export const columnFactories = Object.freeze({
  createNameColumn(options?: { defaultKind?: string }): TableColumn<EntityTableRow> {
    function formatContent(entity: Entity): string {
      return (
        entity.metadata?.title ||
        humanizeEntityRef(entity, {
          defaultKind: options?.defaultKind,
        })
      );
    }

    return {
      title: 'Name',
      field: 'resolved.name',
      highlight: true,
      customSort({ entity: entity1 }, { entity: entity2 }) {
        // TODO: We could implement this more efficiently by comparing field by field.
        // This has similar issues as above.
        return formatContent(entity1).localeCompare(formatContent(entity2));
      },
      render: ({ entity }) => (
        <EntityRefLink
          entityRef={entity}
          defaultKind={options?.defaultKind || 'Component'}
          title={entity.metadata?.title}
        />
      ),
    };
  },
  createSystemColumn(): TableColumn<EntityTableRow> {
    return {
      title: 'System',
      field: 'resolved.partOfSystemRelationTitle',
      render: ({ resolved }) => <EntityRefLinks entityRefs={resolved.partOfSystemRelations} defaultKind="system" />,
    };
  },
  createOwnerColumn(): TableColumn<EntityTableRow> {
    return {
      title: 'Owner',
      field: 'resolved.ownedByRelationsTitle',
      render: ({ resolved }) => <EntityRefLinks entityRefs={resolved.ownedByRelations} defaultKind="group" />,
    };
  },
  createSpecTargetsColumn(): TableColumn<EntityTableRow> {
    return {
      title: 'Targets',
      field: 'entity.spec.targets',
      render: ({ entity }) => (
        <>
          {(entity?.spec?.targets || entity?.spec?.target) && (
            <OverflowTooltip
              text={((entity!.spec!.targets as JsonArray) || [entity.spec.target]).join(', ')}
              placement="bottom-start"
            />
          )}
        </>
      ),
    };
  },
  createSpecTypeColumn(): TableColumn<EntityTableRow> {
    return {
      title: 'Type',
      field: 'entity.spec.type',
      hidden: true,
      width: 'auto',
    };
  },
  createSpecLifecycleColumn(): TableColumn<EntityTableRow> {
    return {
      title: 'Lifecycle',
      field: 'entity.spec.lifecycle',
    };
  },
  createMetadataDescriptionColumn(): TableColumn<EntityTableRow> {
    return {
      title: 'Description',
      field: 'entity.metadata.description',
      render: ({ entity }) => <OverflowTooltip text={entity.metadata.description} placement="bottom-start" />,
      width: 'auto',
    };
  },
  createMetadataCustomerColumn(): TableColumn<EntityTableRow> {
    return {
      title: 'Customer',
      field: 'entity.metadata.customer',
    };
  },
  createTagsColumn(): TableColumn<EntityTableRow> {
    return {
      title: 'Tags',
      field: 'entity.metadata.tags',
      cellStyle: {
        padding: '0px 16px 0px 20px',
      },
      render: ({ entity }) => (
        <>
          {entity.metadata.tags &&
            entity.metadata.tags.map(t => (
              <Chip key={t} label={t} size="small" variant="outlined" style={{ marginBottom: '0px' }} />
            ))}
        </>
      ),
      width: 'auto',
    };
  },

  createTitleColumn(options?: { hidden?: boolean }): TableColumn<EntityTableRow> {
    return {
      title: 'Title',
      field: 'entity.metadata.title',
      hidden: options?.hidden,
      searchable: true,
    };
  },
  createLabelColumn(key: string, options?: { title?: string; defaultValue?: string }): TableColumn<EntityTableRow> {
    return {
      title: options?.title || 'Label',
      field: 'entity.metadata.labels',
      cellStyle: {
        padding: '0px 16px 0px 20px',
      },
      render: ({ entity }: { entity: Entity }) => {
        const labels: Record<string, string> | undefined = entity.metadata?.labels;
        const specifiedLabelValue = (labels && labels[key]) || options?.defaultValue;
        return (
          <>
            {specifiedLabelValue && (
              <Chip key={specifiedLabelValue} label={specifiedLabelValue} size="small" variant="outlined" />
            )}
          </>
        );
      },
      width: 'auto',
    };
  },
  createNamespaceColumn(): TableColumn<EntityTableRow> {
    return {
      title: 'Namespace',
      field: 'entity.metadata.namespace',
      width: 'auto',
    };
  },

  createEntitySpecificColumns(entities: Entity[], kind: string | undefined): TableColumn<EntityTableRow>[] {
    const baseColumns = [
      columnFactories.createSystemColumn(),
      columnFactories.createOwnerColumn(),
      columnFactories.createSpecTypeColumn(),
      columnFactories.createSpecLifecycleColumn(),
    ];
    switch (kind) {
      case 'user':
        return [];
      case 'domain':
      case 'system':
        return [columnFactories.createOwnerColumn(), columnFactories.createMetadataCustomerColumn()];
      case 'group':
      case 'template':
        return [columnFactories.createSpecTypeColumn()];
      case 'location':
        return [columnFactories.createSpecTypeColumn(), columnFactories.createSpecTargetsColumn()];
      default:
        return entities.every(entity => entity.metadata.namespace === 'default')
          ? baseColumns
          : [...baseColumns, columnFactories.createNamespaceColumn()];
    }
  },
});
